import { useCallback } from 'react';
import { gardensApi } from '../services/api';
import { putGarden, getGarden } from '../services/offlineDb';
import { useGardenSettings } from '../context/GardenSettingsContext';
import { usePlant } from '../context/PlantContext';
import { useDashboard } from '../context/DashboardContext';
import { useWizard } from '../context/WizardContext';
import { mapBackendToFrontend } from '../lib/gardenMappers';
import {
  calculatePolygonArea,
  calculateDimensionsFromOutline,
} from '../utils/dimensionCalculations';

/**
 * useGardenLoader — lightweight hook for loading a garden from the backend.
 *
 * Extracts only the load path from useGardenPersistence so it can be consumed
 * independently (e.g. in GardenSwitcher) without starting a second auto-save
 * interval or requiring a Fabric.js canvas ref.
 *
 * #570 (Stufe 1): a successful backend load is written through to IndexedDB
 * (offlineDb). On a network error the loader falls back to that cache so a
 * garden opened before can still be viewed on an offline cold start. Personal
 * garden data deliberately never enters the Service-Worker cache (SEC-19) — the
 * IndexedDB copy is purged on logout (see AuthContext).
 *
 * @returns {{ loadFromBackend: (id: string) => Promise<object|null> }}
 */
export default function useGardenLoader() {
  const { setAllSettings, setGardenId, setGardenVersion, setCanvasObjects } = useGardenSettings();
  const { setAllFavorites } = usePlant();
  const { setPlacedPlants } = useDashboard();
  const { loadGardenData } = useWizard();

  /**
   * Hydrate all garden-related contexts from a raw backend garden record.
   *
   * Shared by the online path (fresh fetch) and the offline path (#570: record
   * read back from IndexedDB). Both receive the identical backend record shape,
   * so hydration — and therefore what the user sees — is the same online and
   * offline.
   *
   * @param {object} backendGarden - raw record from GET /gardens/:id or offlineDb
   * @returns {object|null} canvas_objects for GardenCanvas to load, or null
   */
  const hydrateFromBackendGarden = useCallback((backendGarden) => {
    const frontendData = mapBackendToFrontend(backendGarden);

    // Recalculate garden dimensions from outline vertices.
    // We don't rely on the stored calculatedDimensions because it may be
    // missing (older gardens) or out of date. Recalculating here is cheap
    // and always accurate.
    const outlineVertices = frontendData.outline?.vertices;
    const calibrationScale = frontendData.backgroundImage?.calibration?.scale;
    const dims = outlineVertices?.length >= 3
      ? calculateDimensionsFromOutline(outlineVertices, calibrationScale, calculatePolygonArea)
      : { areaSquareMeters: 0, lengthMeters: 0, widthMeters: 0 };

    // Hydrate GardenSettingsContext
    setAllSettings({
      name: frontendData.name,
      soilType: frontendData.soilType,
      phValue: frontendData.phValue,
      drainage: frontendData.drainage,
      orientation: frontendData.orientation,
      zipCode: frontendData.zipCode,
      city: frontendData.city,
      ...(frontendData.country && { country: frontendData.country }),
      ...(frontendData.hardinessZone && { hardinessZone: frontendData.hardinessZone }),
      ...(frontendData.sunlight && { sunlight: frontendData.sunlight }),
      backgroundImage: frontendData.backgroundImage,
      outline: frontendData.outline,
      heightDifferences: frontendData.heightDifferences,
      // Layer view-state (#466) — null for legacy gardens; LayerContext then
      // keeps its localStorage/default state. setGardenId + setAllSettings are
      // batched in this callback, so LayerContext's gardenId-keyed hydration
      // effect sees settings.layers in the same render.
      ...(frontendData.layers && { layers: frontendData.layers }),
      area: dims.areaSquareMeters,
      length: dims.lengthMeters,
      width: dims.widthMeters,
    });

    // Hydrate garden ID + optimistic-lock version (#496)
    setGardenId(frontendData.gardenId);
    setGardenVersion(backendGarden.version ?? null);

    // Hydrate WizardContext so "Einstellungen bearbeiten" shows this garden's data
    loadGardenData({
      basics: {
        name: frontendData.name,
        soilType: frontendData.soilType,
        phValue: frontendData.phValue,
        drainage: frontendData.drainage,
        orientation: frontendData.orientation,
        sunlight: frontendData.sunlight || null,
        zipCode: frontendData.zipCode,
        city: frontendData.city,
        country: frontendData.country || null,
        hardinessZone: frontendData.hardinessZone || null,
      },
      backgroundImage: frontendData.backgroundImage || undefined,
      outline: frontendData.outline || undefined,
      heightDifferences: frontendData.heightDifferences || undefined,
    });

    // Hydrate favorites — always set to the loaded garden's exact set, even
    // when empty. The previous `length > 0` guard let a leftover global
    // localStorage favorites list survive a load of a garden without
    // favorites, so the editor showed stale favorites from an earlier garden.
    setAllFavorites(frontendData.favorites);

    // Hydrate placedPlants for Dashboard (same logic as DashboardContext mount seed)
    const canvasObjects = backendGarden.canvas_objects;
    const plantData = canvasObjects?.objects
      ?.filter(obj => obj.objectType === 'plant' && obj.plantId)
      .map(obj => ({ plantId: obj.plantId, id: obj.plantId })) ?? [];
    setPlacedPlants(plantData);

    // Hydrate canvasObjects in context. GardenCanvas's externalCanvasData effect
    // watches this and clears+reloads the editor canvas — so the user-drawn
    // objects of the previous garden are removed regardless of who triggered
    // the load (mount, GardenSwitcher, GardenSwitcherSheet). Fixes #428.
    setCanvasObjects(canvasObjects || null);

    // Return canvas objects for backward compatibility with existing callsites.
    return canvasObjects || null;
  }, [setAllSettings, setGardenId, setGardenVersion, setCanvasObjects, setAllFavorites, setPlacedPlants, loadGardenData]);

  /**
   * Load garden from backend and hydrate contexts.
   * @param {string} id - Garden UUID
   * @returns {object|null} canvas_objects for GardenCanvas to load, or null
   */
  const loadFromBackend = useCallback(async (id) => {
    try {
      const backendGarden = await gardensApi.getById(id);
      // #570: write-through to the offline cache so this garden is viewable on a
      // later offline cold start. putGarden never throws (best-effort).
      await putGarden(backendGarden);
      return hydrateFromBackendGarden(backendGarden);
    } catch (error) {
      if (error.message?.includes('404')) {
        // Garden not found — clear stale ID and fall back to localStorage silently
        console.warn('Garden not found on backend, clearing stored ID');
        setGardenId(null);
        setGardenVersion(null);
        return null;
      }

      // Network / server error. #570: if we have an offline copy, hydrate from it
      // instead of failing — the caller (GardenStartupLoader / GardenSwitcher)
      // then shows the cached garden. The read-only offline indication is wired
      // separately (Chunk C). A cache miss re-throws so the caller can surface
      // the error / OfflineEmptyScreen.
      const cached = await getGarden(id);
      if (cached) {
        console.info('Loading garden from offline cache (#570):', error.message);
        return hydrateFromBackendGarden(cached);
      }

      console.warn('Failed to load garden from backend:', error.message);
      throw error;
    }
  }, [hydrateFromBackendGarden, setGardenId, setGardenVersion]);

  return { loadFromBackend };
}
